/**
 * AWS Partner Support (category SUPPORT, type AWS_PARTNER_SUPPORT).
 * API: configuration.parameters uses numeric tiers and optional minimum_charge.
 */

export const AWS_PARTNER_SUPPORT_RULE_TYPE = "AWS_PARTNER_SUPPORT";
export const SUPPORT_RULE_CATEGORY = "SUPPORT";

export const CHARGE_SELECTION_GREATER = "GREATER_OF_MINIMUM_OR_TIERED";

export const AWS_PARTNER_SUPPORT_PLAN_PRESETS = {
  BUSINESS_SUPPORT: {
    minimum_charge: { amount: 29, unit: "PER_ACCOUNT_PER_MONTH" },
    tiers: [
      { from_amount: 0, to_amount: 10000, percent: 9 },
      { from_amount: 10000, to_amount: 80000, percent: 7 },
      { from_amount: 80000, to_amount: 250000, percent: 5 },
      { from_amount: 250000, to_amount: null, percent: 3 },
    ],
  },
  ENTERPRISE_SUPPORT: {
    minimum_charge: { amount: 5000, unit: "PER_MONTH" },
    tiers: [
      { from_amount: 0, to_amount: 150000, percent: 10 },
      { from_amount: 150000, to_amount: 500000, percent: 7 },
      { from_amount: 500000, to_amount: 1000000, percent: 5 },
      { from_amount: 1000000, to_amount: null, percent: 3 },
    ],
  },
  UNIFIED_OPERATIONS: {
    minimum_charge: { amount: 50000, unit: "PER_MONTH" },
    tiers: [
      { from_amount: 0, to_amount: 1000000, percent: 10 },
      { from_amount: 1000000, to_amount: 5000000, percent: 6 },
      { from_amount: 5000000, to_amount: null, percent: 5 },
    ],
  },
};

export function isAwsPartnerSupportRule(category, type) {
  return category === SUPPORT_RULE_CATEGORY && type === AWS_PARTNER_SUPPORT_RULE_TYPE;
}

function formatUsdPlain(n) {
  const num = Number(n);
  if (!Number.isFinite(num)) return String(n);
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(num);
}

export function formatUsdCompact(n) {
  const num = Number(n);
  if (!Number.isFinite(num)) return "—";
  const sign = num < 0 ? "-" : "";
  const v = Math.abs(num);
  if (v >= 1_000_000) {
    const m = v / 1_000_000;
    const s = Number.isInteger(m) ? String(m) : m.toFixed(1).replace(/\.0$/, "");
    return `${sign}$${s}M`;
  }
  if (v >= 1000) {
    const k = v / 1000;
    const s = Number.isInteger(k) ? String(k) : k.toFixed(1).replace(/\.0$/, "");
    return `${sign}$${s}K`;
  }
  return `${sign}$${Math.round(v)}`;
}

export function effectiveMinimumChargeValues(planKey, mc) {
  const preset = AWS_PARTNER_SUPPORT_PLAN_PRESETS[planKey];
  if (!preset) return { amount: 0, unit: "PER_MONTH" };
  const raw = mc && typeof mc === "object" ? mc.amount : undefined;
  const useDefault = raw === "" || raw === null || raw === undefined;
  const amount = useDefault ? preset.minimum_charge.amount : Number(raw);
  const unit = (mc && mc.unit) || preset.minimum_charge.unit;
  return {
    amount: Number.isFinite(amount) ? amount : preset.minimum_charge.amount,
    unit: String(unit || preset.minimum_charge.unit),
  };
}

export function formatMinimumSpendReadable(planKey, mc) {
  const { amount, unit } = effectiveMinimumChargeValues(planKey, mc);
  if (unit === "PER_ACCOUNT_PER_MONTH") {
    return `$${amount}/month per account`;
  }
  if (amount >= 1000 && amount % 1000 === 0) {
    return `$${amount / 1000}k/month`;
  }
  return `$${amount}/month`;
}

export function formatPartnerSupportTierLinesAwsStyle(tiers) {
  if (!Array.isArray(tiers) || tiers.length === 0) return [];
  return tiers.map((t) => {
    const pctRaw = t.percent;
    const pct = pctRaw === "" || pctRaw === null || pctRaw === undefined ? null : Number(pctRaw);
    if (!Number.isFinite(pct)) return "—";
    const from = Number(t.from_amount);
    const fromOk = Number.isFinite(from);
    const toRaw = t.to_amount;
    const hasCap = toRaw !== null && toRaw !== undefined && toRaw !== "";
    const to = hasCap ? Number(toRaw) : null;
    const toOk = hasCap && Number.isFinite(to);
    if (fromOk && from <= 0 && toOk) {
      return `${pct}% of monthly AWS charges up to ${formatUsdCompact(to)}`;
    }
    if (fromOk && toOk) {
      return `${pct}% of monthly AWS charges from ${formatUsdCompact(from)} to ${formatUsdCompact(to)}`;
    }
    if (fromOk && !hasCap) {
      return `${pct}% of monthly AWS charges over ${formatUsdCompact(from)}`;
    }
    return `${pct}% of monthly AWS charges`;
  });
}

export function formatAwsPartnerSupportParameterDisplay(key, value) {
  if (key === "tiers" && Array.isArray(value)) {
    return value
      .map((t) => {
        const to = t.to_amount == null || t.to_amount === "" ? "∞" : formatUsdPlain(t.to_amount);
        return `${Number(t.percent)}% on ${formatUsdPlain(t.from_amount)} → ${to}`;
      })
      .join(" · ");
  }
  if (key === "minimum_charge" && value && typeof value === "object") {
    const a = value.amount;
    const u = value.unit || "";
    return `$${a} (${String(u).replace(/_/g, " ").toLowerCase()})`;
  }
  if (value != null && typeof value === "object") {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  return value === undefined || value === null ? "" : String(value);
}

function cloneTiers(tiers) {
  return (tiers || []).map((t) => ({
    from_amount: t.from_amount,
    to_amount: t.to_amount === undefined ? null : t.to_amount,
    percent: t.percent,
  }));
}

export function buildAwsPartnerSupportDefaultParameters(planName = "BUSINESS_SUPPORT") {
  const key = String(planName || "BUSINESS_SUPPORT").toUpperCase();
  const preset = AWS_PARTNER_SUPPORT_PLAN_PRESETS[key] || AWS_PARTNER_SUPPORT_PLAN_PRESETS.BUSINESS_SUPPORT;
  const plan = AWS_PARTNER_SUPPORT_PLAN_PRESETS[key] ? key : "BUSINESS_SUPPORT";
  return {
    plan_name: plan,
    charge_selection: CHARGE_SELECTION_GREATER,
    minimum_charge: { ...preset.minimum_charge },
    tiers: cloneTiers(preset.tiers),
  };
}

export const AWS_PARTNER_SUPPORT_PLAN_KEYS = Object.keys(AWS_PARTNER_SUPPORT_PLAN_PRESETS);

/** Matrix for carrier-style UI: one editable config per plan; `activePlanKey` is what the API rule uses. */
export function buildAwsPartnerSupportMatrixFromParameters(parameters) {
  const keys = [...AWS_PARTNER_SUPPORT_PLAN_KEYS];
  const p = parameters && typeof parameters === "object" ? parameters : {};
  const rawPlan = String(p.plan_name || "BUSINESS_SUPPORT").toUpperCase();
  const activePlanKey = keys.includes(rawPlan) ? rawPlan : "BUSINESS_SUPPORT";
  const byPlan = {};
  for (const k of keys) {
    if (k === activePlanKey) {
      const preset = buildAwsPartnerSupportDefaultParameters(k);
      byPlan[k] = {
        plan_name: k,
        charge_selection: p.charge_selection ?? preset.charge_selection,
        minimum_charge:
          p.minimum_charge && typeof p.minimum_charge === "object"
            ? { ...preset.minimum_charge, ...p.minimum_charge }
            : { ...preset.minimum_charge },
        tiers:
          Array.isArray(p.tiers) && p.tiers.length > 0 ? cloneTiers(p.tiers) : cloneTiers(preset.tiers),
      };
    } else {
      byPlan[k] = buildAwsPartnerSupportDefaultParameters(k);
    }
  }
  return { activePlanKey, byPlan };
}

export function getActivePlanParametersFromMatrix(matrix) {
  const keys = AWS_PARTNER_SUPPORT_PLAN_KEYS;
  const fallbackKey = "BUSINESS_SUPPORT";
  const key =
    matrix?.activePlanKey && keys.includes(matrix.activePlanKey) ? matrix.activePlanKey : fallbackKey;
  const raw = matrix?.byPlan?.[key];
  const preset = buildAwsPartnerSupportDefaultParameters(key);
  if (!raw || typeof raw !== "object") {
    return preset;
  }
  return {
    plan_name: key,
    charge_selection: raw.charge_selection ?? preset.charge_selection,
    minimum_charge:
      raw.minimum_charge && typeof raw.minimum_charge === "object"
        ? { ...preset.minimum_charge, ...raw.minimum_charge }
        : { ...preset.minimum_charge },
    tiers:
      Array.isArray(raw.tiers) && raw.tiers.length > 0 ? cloneTiers(raw.tiers) : cloneTiers(preset.tiers),
  };
}

function num(v) {
  if (v === "" || v === null || v === undefined) return NaN;
  const n = typeof v === "number" ? v : parseFloat(String(v).replace(/[^0-9.-]/g, ""));
  return Number.isFinite(n) ? n : NaN;
}

export function validateAwsPartnerSupportParameters(parameters) {
  const p = parameters && typeof parameters === "object" ? parameters : {};
  const plan = String(p.plan_name || "").toUpperCase();
  if (!AWS_PARTNER_SUPPORT_PLAN_PRESETS[plan]) {
    return { ok: false, message: "Select a valid partner support plan." };
  }
  if (!p.charge_selection) {
    return { ok: false, message: "Charge selection is required." };
  }
  const tiers = Array.isArray(p.tiers) ? p.tiers : [];
  if (tiers.length === 0) {
    return { ok: false, message: "Support tiers are required." };
  }
  for (let i = 0; i < tiers.length; i += 1) {
    const t = tiers[i];
    const pct = num(t.percent);
    if (!Number.isFinite(pct) || pct < 0 || pct > 100) {
      return { ok: false, message: `Tier ${i + 1}: enter a valid percent (0–100).` };
    }
    const from = num(t.from_amount);
    if (!Number.isFinite(from) || from < 0) {
      return { ok: false, message: `Tier ${i + 1}: invalid from amount.` };
    }
    if (t.to_amount != null && t.to_amount !== "") {
      const to = num(t.to_amount);
      if (!Number.isFinite(to) || to <= from) {
        return {
          ok: false,
          message: `Tier ${i + 1}: to amount must be greater than from amount, or leave empty for open-ended.`,
        };
      }
    }
  }
  const mc = p.minimum_charge;
  if (mc != null && typeof mc === "object" && mc.amount !== "" && mc.amount != null) {
    const amt = num(mc.amount);
    if (!Number.isFinite(amt) || amt < 0) {
      return { ok: false, message: "Minimum charge amount must be a valid number." };
    }
    if (mc.unit && !["PER_ACCOUNT_PER_MONTH", "PER_MONTH"].includes(String(mc.unit))) {
      return { ok: false, message: "Minimum charge unit must be PER_ACCOUNT_PER_MONTH or PER_MONTH." };
    }
  }
  return { ok: true };
}

/** Shape sent to POST /rules (numeric tiers, optional minimum_charge). */
export function sanitizeAwsPartnerSupportParametersForApi(parameters) {
  const raw = parameters && typeof parameters === "object" ? { ...parameters } : {};
  const plan = String(raw.plan_name || "BUSINESS_SUPPORT").toUpperCase();
  const preset = AWS_PARTNER_SUPPORT_PLAN_PRESETS[plan] || AWS_PARTNER_SUPPORT_PLAN_PRESETS.BUSINESS_SUPPORT;
  const planKey = AWS_PARTNER_SUPPORT_PLAN_PRESETS[plan] ? plan : "BUSINESS_SUPPORT";

  const tiers = (Array.isArray(raw.tiers) ? raw.tiers : preset.tiers).map((t) => ({
    from_amount: Number(t.from_amount),
    to_amount:
      t.to_amount === null || t.to_amount === undefined || t.to_amount === ""
        ? null
        : Number(t.to_amount),
    percent: Number(t.percent),
  }));

  let minimum_charge;
  const mc = raw.minimum_charge;
  if (mc && typeof mc === "object" && mc.amount !== "" && mc.amount != null) {
    minimum_charge = {
      amount: Number(mc.amount),
      unit: String(mc.unit || preset.minimum_charge.unit),
    };
  }

  const out = {
    plan_name: planKey,
    charge_selection: String(raw.charge_selection || CHARGE_SELECTION_GREATER),
    tiers,
  };
  if (minimum_charge) out.minimum_charge = minimum_charge;
  return out;
}

export function normalizeAwsPartnerSupportParameters(parameters) {
  let p = parameters && typeof parameters === "object" ? { ...parameters } : {};
  if (typeof p.tiers === "string") {
    try {
      p.tiers = JSON.parse(p.tiers);
    } catch {
      p.tiers = [];
    }
  }
  if (!Array.isArray(p.tiers) || p.tiers.length === 0) {
    p = buildAwsPartnerSupportDefaultParameters(String(p.plan_name || "BUSINESS_SUPPORT"));
  }
  if (!p.charge_selection) p.charge_selection = CHARGE_SELECTION_GREATER;
  const plan = String(p.plan_name || "BUSINESS_SUPPORT").toUpperCase();
  if (!AWS_PARTNER_SUPPORT_PLAN_PRESETS[plan]) {
    p.plan_name = "BUSINESS_SUPPORT";
  } else {
    p.plan_name = plan;
  }
  if (!p.minimum_charge || typeof p.minimum_charge !== "object") {
    const preset = AWS_PARTNER_SUPPORT_PLAN_PRESETS[p.plan_name];
    p.minimum_charge = { ...preset.minimum_charge };
  } else {
    p.minimum_charge = {
      amount: p.minimum_charge.amount ?? AWS_PARTNER_SUPPORT_PLAN_PRESETS[p.plan_name].minimum_charge.amount,
      unit: p.minimum_charge.unit || AWS_PARTNER_SUPPORT_PLAN_PRESETS[p.plan_name].minimum_charge.unit,
    };
  }
  return p;
}
